"use client";

import { motion } from "framer-motion";

const CARD_META = [
  {
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth={1.4} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v6m3-3H9m12 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
      </svg>
    ),
    iconBg: "bg-violet-500/15 border-violet-500/25 text-violet-400",
    topBar: "from-violet-500/40 to-transparent",
    border: "hover:border-violet-500/30",
    num: "text-violet-500/50",
  },
  {
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth={1.4} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008Zm0 3h.008v.008h-.008v-.008Zm0 3h.008v.008h-.008v-.008Z" />
      </svg>
    ),
    iconBg: "bg-cyan-500/15 border-cyan-500/25 text-cyan-400",
    topBar: "from-cyan-500/40 to-transparent",
    border: "hover:border-cyan-500/30",
    num: "text-cyan-500/50",
  },
  {
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth={1.4} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v17.25m0 0c-1.472 0-2.882.265-4.185.75M12 20.25c1.472 0 2.882.265 4.185.75M18.75 4.97A48.416 48.416 0 0 0 12 4.5c-2.291 0-4.545.16-6.75.47m13.5 0c1.01.143 2.01.317 3 .52m-3-.52 2.62 10.726c.122.499-.106 1.028-.589 1.202a5.988 5.988 0 0 1-2.031.352 5.988 5.988 0 0 1-2.031-.352c-.483-.174-.711-.703-.59-1.202L18.75 4.97Zm-12.5 0L8.87 15.696c.122.499-.106 1.028-.589 1.202a5.989 5.989 0 0 1-2.031.352 5.989 5.989 0 0 1-2.031-.352c-.483-.174-.711-.703-.59-1.202L6.25 4.97Z" />
      </svg>
    ),
    iconBg: "bg-amber-500/15 border-amber-500/25 text-amber-400",
    topBar: "from-amber-500/40 to-transparent",
    border: "hover:border-amber-500/30",
    num: "text-amber-500/50",
  },
  {
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth={1.4} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456Z" />
      </svg>
    ),
    iconBg: "bg-emerald-500/15 border-emerald-500/25 text-emerald-400",
    topBar: "from-emerald-500/40 to-transparent",
    border: "hover:border-emerald-500/30",
    num: "text-emerald-500/50",
  },
];

export default function VoiceAgentUseCases({ heading, items }) {
  const cards = items || [];

  return (
    <section className="bg-zinc-900/40 py-24 border-t border-white/[0.04]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div>
            <p className="text-sm font-semibold text-violet-400 uppercase tracking-widest mb-3">Use Cases</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-white">{heading || "Who it's for"}</h2>
          </div>
          <p className="text-zinc-500 text-sm max-w-xs lg:text-right">
            Built for service-based businesses where every call is a revenue opportunity.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {cards.map((item, i) => {
            const meta = CARD_META[i % CARD_META.length];
            return (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className={`group relative rounded-2xl border border-white/[0.07] bg-zinc-900/70 overflow-hidden transition-all duration-300 ${meta.border}`}
              >
                {/* Thin colour bar at top */}
                <div className={`h-px w-full bg-gradient-to-r ${meta.topBar}`} />

                <div className="p-7">
                  {/* Icon + number row */}
                  <div className="flex items-start justify-between mb-5">
                    <div className={`w-12 h-12 rounded-xl border flex items-center justify-center ${meta.iconBg}`}>
                      {meta.icon}
                    </div>
                    <span className={`text-xs font-bold tabular-nums mt-1 ${meta.num}`}>
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>

                  <h3 className="text-base font-semibold text-white mb-2 leading-snug">
                    {item.title}
                  </h3>
                  <p className="text-sm text-zinc-500 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>

      </div>
    </section>
  );
}
